import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  DollarSign, 
  AlertCircle, 
  Clock, 
  TrendingUp,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Calendar,
  BarChart3
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';

// Helper function to format currency
const formatCurrency = (amount) => {
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`;
  } else if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(1)}K`;
  }
  return `$${(amount || 0).toLocaleString()}`;
};

// MetricCard component - defined outside to avoid recreation on each render
const MetricCard = ({ icon: Icon, label, value, subValue, color = 'default', onClick }) => (
  <motion.div
    className={cn(
      "bg-card border border-border rounded-xl p-4 hover:border-primary/30 transition-all",
      onClick && "cursor-pointer hover:shadow-md"
    )}
    onClick={onClick}
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    whileHover={onClick ? { scale: 1.02 } : {}}
  >
    <div className="flex items-start justify-between">
      <div className={cn(
        "w-10 h-10 rounded-lg flex items-center justify-center",
        color === 'positive' && "bg-emerald-500/10",
        color === 'warning' && "bg-amber-500/10",
        color === 'negative' && "bg-rose-500/10",
        color === 'info' && "bg-blue-500/10",
        color === 'default' && "bg-muted"
      )}>
        <Icon className={cn(
          "w-5 h-5",
          color === 'positive' && "text-emerald-500",
          color === 'warning' && "text-amber-500",
          color === 'negative' && "text-rose-500",
          color === 'info' && "text-blue-500",
          color === 'default' && "text-muted-foreground"
        )} />
      </div>
    </div>
    <div className="mt-3">
      <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold font-mono mt-1">{value}</p>
      {subValue && <p className="text-xs text-muted-foreground mt-1">{subValue}</p>}
    </div>
  </motion.div>
);

/**
 * ARAPDashboardHeader - Dashboard-style header for Invoices (AR) and Bills (AP) pages
 * Shows key metrics, aging chart, and AI Buck insights
 */
export function ARAPDashboardHeader({
  type = 'ar', // 'ar' for receivables (invoices) or 'ap' for payables (bills)
  metrics = {},
  aging = {},
  insight = null,
  upcomingItems = [],
  loading = false
}) {
  const navigate = useNavigate();
  const isAR = type === 'ar';
  
  // Default metrics based on type
  const defaultMetrics = isAR ? {
    total: 0,
    overdue: 0,
    collected: 0,
    avgDays: 0
  } : {
    total: 0,
    dueThisWeek: 0,
    overdue: 0,
    paidThisMonth: 0
  };
  
  const m = { ...defaultMetrics, ...metrics };
  
  // Aging buckets
  const agingBuckets = [
    { key: 'current', label: 'Current', color: 'emerald' },
    { key: 'days_1_30', label: '1-30 days', color: 'blue' },
    { key: 'days_31_60', label: '31-60 days', color: 'amber' },
    { key: 'days_61_90', label: '61-90 days', color: 'orange' },
    { key: 'days_90_plus', label: '90+ days', color: 'rose' }
  ];
  
  // Calculate total for aging chart
  const agingTotal = Object.values(aging).reduce((sum, val) => sum + (val || 0), 0);
  
  if (loading) {
    return (
      <div className="space-y-4 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-card border border-border rounded-xl p-4">
              <div className="w-10 h-10 bg-muted rounded-lg animate-pulse" />
              <div className="mt-3 space-y-2">
                <div className="h-3 w-20 bg-muted rounded animate-pulse" />
                <div className="h-7 w-24 bg-muted rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-4 mb-6" data-testid="arap-dashboard-header">
      {/* Key Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {isAR ? (
          <>
            <MetricCard
              icon={DollarSign}
              label="Total Outstanding"
              value={formatCurrency(m.total)}
              color="default"
            />
            <MetricCard
              icon={AlertCircle}
              label="Overdue"
              value={formatCurrency(m.overdue)}
              subValue={m.overdueCount ? `${m.overdueCount} invoices` : null}
              color={m.overdue > 0 ? 'negative' : 'default'}
              onClick={m.overdue > 0 ? () => navigate('/invoices?status=overdue') : null}
            />
            <MetricCard
              icon={CheckCircle2}
              label="Collected This Month"
              value={formatCurrency(m.collected)}
              color="positive"
            />
            <MetricCard
              icon={Clock}
              label="Avg Days to Pay"
              value={`${m.avgDays || 0} days`}
              color="info"
            />
          </>
        ) : (
          <>
            <MetricCard
              icon={DollarSign}
              label="Total Payables"
              value={formatCurrency(m.total)}
              color="default"
            />
            <MetricCard
              icon={Calendar}
              label="Due This Week"
              value={formatCurrency(m.dueThisWeek)}
              subValue={m.dueThisWeekCount ? `${m.dueThisWeekCount} bills` : null}
              color="warning"
              onClick={m.dueThisWeek > 0 ? () => navigate('/bills?due=week') : null}
            />
            <MetricCard
              icon={AlertCircle}
              label="Overdue"
              value={formatCurrency(m.overdue)}
              subValue={m.overdueCount ? `${m.overdueCount} bills` : null}
              color={m.overdue > 0 ? 'negative' : 'default'}
              onClick={m.overdue > 0 ? () => navigate('/bills?status=overdue') : null}
            />
            <MetricCard
              icon={CheckCircle2}
              label="Paid This Month"
              value={formatCurrency(m.paidThisMonth)}
              color="positive"
            />
          </>
        )}
      </div>
      
      {/* Aging Chart & AI Buck Insight */}
      <div className="grid md:grid-cols-3 gap-4">
        {/* Aging Bar Chart */}
        <div className="md:col-span-2 bg-card border border-border rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-muted-foreground" />
              <h3 className="font-medium text-sm">{isAR ? 'AR' : 'AP'} Aging Summary</h3>
            </div>
            <span className="text-xs text-muted-foreground">
              Total: {formatCurrency(agingTotal)}
            </span>
          </div>
          
          {/* Aging bars */}
          <div className="space-y-3">
            {agingBuckets.map((bucket) => {
              const value = aging[bucket.key] || 0;
              const percentage = agingTotal > 0 ? (value / agingTotal) * 100 : 0;
              
              return (
                <div key={bucket.key} className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground w-20">{bucket.label}</span>
                  <div className="flex-1 h-6 bg-muted rounded-full overflow-hidden">
                    <motion.div
                      className={cn(
                        "h-full rounded-full",
                        bucket.color === 'emerald' && "bg-emerald-500",
                        bucket.color === 'blue' && "bg-blue-500",
                        bucket.color === 'amber' && "bg-amber-500",
                        bucket.color === 'orange' && "bg-orange-500",
                        bucket.color === 'rose' && "bg-rose-500"
                      )}
                      initial={{ width: 0 }}
                      animate={{ width: `${percentage}%` }}
                      transition={{ duration: 0.5, delay: 0.1 }}
                    />
                  </div>
                  <span className="text-xs font-mono w-20 text-right">
                    {formatCurrency(value)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* AI Buck Insight or Upcoming Items */}
        <div className="bg-card border border-border rounded-xl p-4">
          {insight ? (
            <div className="h-full flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-violet-500/10 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-violet-500" />
                </div>
                <span className="text-xs font-semibold text-violet-500 uppercase tracking-wide">AI Buck Insight</span>
              </div>
              <p className="text-sm text-muted-foreground flex-1">{insight.message}</p>
              {insight.action && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(insight.action.path)}
                  className="mt-3 w-full justify-between text-violet-500 hover:text-violet-600 hover:bg-violet-500/5"
                >
                  {insight.action.label}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              )}
            </div>
          ) : upcomingItems.length > 0 ? (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">
                  {isAR ? 'Due Soon' : 'Upcoming Bills'}
                </span>
              </div>
              <div className="space-y-2">
                {upcomingItems.slice(0, 4).map((item, idx) => (
                  <div key={item.id || idx} className="flex items-center justify-between text-sm">
                    <span className="truncate text-muted-foreground">{item.name}</span>
                    <span className="font-mono text-xs">{formatCurrency(item.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-500 mb-2" />
              <p className="text-sm font-medium">All Clear</p>
              <p className="text-xs text-muted-foreground mt-1">
                {isAR ? 'No overdue invoices' : 'No urgent bills'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ARAPDashboardHeader;
